import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { IAttachment, IAttachmentTypes, IInsertAttachment, IInsertAttachmentResponse } from '../models/attachment-types.model';

@Injectable({
  providedIn: 'root',
})
export class OfficeService {
  private http = inject(HttpClient);

  public getAttachmentList(objectParams: { co: number; form: string; keyID: number; UniqueAttchID: string }): Observable<IAttachment[]> {
    const params = new HttpParams({ fromObject: objectParams });
    return this.http.get<IAttachment[]>('/Office/GetAttachmentList', { params });
  }

  public insertAttachment(attachmentData: IInsertAttachment): Observable<IInsertAttachmentResponse> {
    return this.http.post<IInsertAttachmentResponse>('/Office/InsertAttachment', attachmentData);
  }

  public updateAttachmentMetadata(
    co: number,
    form: string,
    keyID: number,
    description: string,
    attachmentID: number,
    attachmentTypeID: number
  ): Observable<never> {
    return this.http.post<never>('/Office/UpdateAttachmentMetadata', {
      co: co,
      form: form,
      keyID: keyID,
      attachmentID: attachmentID,
      description: description,
      attachmentTypeID: attachmentTypeID,
    });
  }

  public deleteAttachment(co: number, form: string, keyID: number, attachmentID: number): Observable<never> {
    return this.http.post<never>('/Office/DeleteAttachment', {
      co: co,
      form: form,
      keyID: keyID,
      attachmentID: attachmentID,
    });
  }

  public getAttachmentTypes(co: number): Observable<IAttachmentTypes[]> {
    const params = new HttpParams().append('co', co);
    return this.http.get<IAttachmentTypes[]>('/Office/AttachmentTypes', { params });
  }

  getCompanies() {
    return this.http.get("/Office/GetCompanies").toPromise();
  }

  getForms(company: any, module: any) {
    return this.http.get("/Office/GetForms?co=" + company + "&module=" + module).toPromise();
  }

  getFormMetadata(tableName: any, company: any) {
    return this.http.get("/Office/GetFormMetadata?form=" + tableName + "&co=" + company).toPromise();
  }

  getFormData(tableName: any, company: any, orderby: any, keyid: any, rowsPerPage: any, pageNumber: any, where: any, parentForm: string, parentFormData: string, includeMetadata: boolean) {
    return this.http.post("/Office/GetData", {
      form: tableName, co: company, rowsPerPage: rowsPerPage, pageNumber: pageNumber, orderby: orderby, keyid: keyid, where: where, parentForm: parentForm,
      parentFormData: parentFormData, includeMetadata: includeMetadata
    }).toPromise();
  }

  insertUpdateData(co: any, form: any, formData: any, keyID: any) {
    return this.http.post("/Office/InsertUpdateData",
      {
        co: co,
        form: form,
        formData: formData,
        keyID: keyID,
      }).toPromise();
  }

  deleteRow(co: any, form: any, keyID: any) {
    return this.http.post("/Office/DeleteData",
      {
        co: co,
        form: form,
        keyID: keyID,
      }).toPromise();
  }

  getAttachmentData(co: any, form: any, keyID: any, attachmentID: any) {
    return this.http.get("/Office/GetAttachmentData?co=" + co + "&form=" + form + "&keyID=" + keyID + "&attachmentID=" + attachmentID).toPromise();
  }

  getFormItemGroups(form: any) {
    return this.http.get("/Office/GetFormItemGroups?form=" + form).toPromise();
  }

  getFormItemFields(form: any) {
    return this.http.get("/Office/GetFormItemFields?form=" + form).toPromise();
  }

  getFormReports(co: any, form: any) {
    return this.http.get("/Office/getFormReports?co=" + co + "&form=" + form).toPromise();
  }

  getLookup(co: any, form: any, seq: any, lookup: any, formData: any, searchString: any) {
    return this.http.post("/Office/getLookup", {
      co: co,
      form: form,
      seq: seq,
      lookup: lookup,
      formData: formData,
      searchString: searchString
    }).toPromise();
  }

  getStaticLookups(co: any, form: any) {
    return this.http.get("/Office/getStaticLookups?co=" + co + "&form=" + form).toPromise();
  }

  getVaInquiries(co) {
    return this.http.get("/Office/GetVaInquiries?co=" + co).toPromise();
  }

  getVaInquiryData(co, queryName, paramValues) {
    let url = "/Office/GetVaInquiryData?co=" + co + "&queryName=" + queryName;
    let params = "";
    for (let i = 0; i < paramValues.length; i++) {
      params += "&paramValues[" + i + "]=" + encodeURIComponent(paramValues[i]);
    }
    return this.http.get(url + params).toPromise();
  }

  getVaInquiryLookup(co, lookup, dependentData = null) {
    let url = "/Office/GetVaInquiryLookup?co=" + co + "&lookup=" + lookup;
    if (dependentData != null)
      url += "&dependentData=" + encodeURIComponent(dependentData);
    return this.http.get(url).toPromise();
  }

  getVaInquiryParameters(queryName) {
    return this.http.get("/Office/GetVaInquiryParameters?queryName=" + queryName).toPromise();
  }

  updateFormState(form, groups, fields) {
    let url = "/Office/UpdateFormState";
    let postData = {
      form: form,
      groups: groups,
      fields: fields
    }
    return this.http.post(url, postData).toPromise();
  }

  getExcelFile(tableName: any, company: any, orderby: any, keyid: any, where: any, columnInfo: any, fileName: any) {
    return this.http.post("/office/SaveExcelFile", {
      form: tableName,
      co: company,
      orderby: orderby,
      keyid: keyid,
      where: where,
      columns: columnInfo
    }).toPromise().then((response) => {
      if (response["success"] == false) {
        return false;
      }
      else {
        let url = "/Office/GetExcelFile?formName=" + tableName + "&fileName=" + (fileName ?? tableName);
        window.location.href = url;
        return true;
      }
    }, (rejectResponse) => {
      return false;
    });
  }

  pendoTrack(formName) {
    if (formName == "1" || formName == null)
      return;

    let url = "/Office/PendoTrack";
    let postData = {
      formName: formName
    }
    return this.http.post(url, postData).toPromise();
  }

  validateAndDefault(co: any, form: any, seq: any, formData: any) {
    return this.http.post("/Office/ValidateAndDefault",
      {
        co: co,
        form: form,
        seq: seq,
        formData: formData
      }).toPromise();
  }
}
